import { useState, useRef, useMemo } from 'react';
import { useTranslate, useGetList, useDataProvider } from 'react-admin';
import {
  Box,
  Paper,
  Tabs,
  Tab,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  Collapse,
  Grid,
  Chip,
  Tooltip,
  type SelectChangeEvent,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import PersonIcon from '@mui/icons-material/Person';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import InboxIcon from '@mui/icons-material/Inbox';
import FilterListIcon from '@mui/icons-material/FilterList';
import SortIcon from '@mui/icons-material/Sort';
import { TicketsList } from './TicketsList';
import { TicketsQueueSelect } from './TicketsQueueSelect';
import { TicketContextMenu } from './TicketContextMenu';
import { CreateConversationDialog } from './dialogs';
import { TicketTabFilter, type TicketsManagerProps, type TicketQueueOption } from '../types/tickets.types';
import { TicketStatus, TicketPriority, type Ticket } from '../../../types/chat.types';

/**
 * TicketsManager - Main container for ticket management
 *
 * Layout (top to bottom):
 * 1. Persistent search bar + New Ticket button
 * 2. Assignment-based tabs (Mine / Unassigned / All)
 * 3. Sort dropdown + Filter toggle
 * 4. Collapsible advanced filters (status, priority, queue)
 * 5. Single TicketsList with all filters applied
 * 6. Context menu for right-click actions
 */
export function TicketsManager({
  onTicketSelect,
  selectedTicketId,
}: TicketsManagerProps) {
  const translate = useTranslate();
  const dataProvider = useDataProvider();

  // Tab
  const [tab, setTab] = useState<TicketTabFilter>(TicketTabFilter.MINE);

  // Persistent search
  const [searchParam, setSearchParam] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const searchTimeoutRef = useRef<NodeJS.Timeout>(undefined);

  // Sort
  const [sortField, setSortField] = useState<'updatedAt' | 'createdAt' | 'priority'>('updatedAt');
  const [sortOrder] = useState<'ASC' | 'DESC'>('DESC');

  // Advanced filters
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState<TicketStatus | null>(null);
  const [priorityFilter, setPriorityFilter] = useState<TicketPriority | null>(null);
  const [selectedQueueIds, setSelectedQueueIds] = useState<number[]>([]);

  // Context menu
  const [contextMenuTicket, setContextMenuTicket] = useState<Ticket | null>(null);
  const [contextMenuPosition, setContextMenuPosition] = useState<{ top: number; left: number } | null>(null);

  // Dialog
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);

  // Fetch available queues
  const { data: queues = [] } = useGetList<TicketQueueOption>('ticket-queue', {
    pagination: { page: 1, perPage: 50 },
    sort: { field: 'name', order: 'ASC' },
  });

  const availableQueues = useMemo<TicketQueueOption[]>(() =>
    queues.map((q) => ({
      id: q.id,
      uuid: q.uuid,
      name: q.name,
      color: q.color,
    })),
  [queues]);

  // Debounced search handler
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInput(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (value === '') {
      setSearchParam('');
      return;
    }

    searchTimeoutRef.current = setTimeout(() => {
      setSearchParam(value.toLowerCase());
    }, 500);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchParam('');
  };

  const handleChangeTab = (_e: React.SyntheticEvent, newValue: TicketTabFilter) => {
    setTab(newValue);
  };

  const handleTicketSelect = (ticket: Ticket) => {
    onTicketSelect?.(ticket);
  };

  const handleContextMenu = (event: React.MouseEvent, ticket: Ticket) => {
    event.preventDefault();
    setContextMenuTicket(ticket);
    setContextMenuPosition({ top: event.clientY, left: event.clientX });
  };

  const handleCloseContextMenu = () => {
    setContextMenuTicket(null);
    setContextMenuPosition(null);
  };

  // Active filter count for badge
  const activeFilterCount = [
    statusFilter,
    priorityFilter,
    selectedQueueIds.length > 0 ? true : null,
  ].filter(Boolean).length;

  const clearAllFilters = () => {
    setStatusFilter(null);
    setPriorityFilter(null);
    setSelectedQueueIds([]);
  };

  return (
    <Paper
      elevation={0}
      variant="outlined"
      sx={{
        position: 'relative',
        display: 'flex',
        height: '100%',
        flexDirection: 'column',
        overflow: 'hidden',
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
      }}
    >
      {/* Create Conversation Dialog */}
      <CreateConversationDialog
        open={newTicketModalOpen}
        onClose={() => setNewTicketModalOpen(false)}
        title={translate('resources.tickets.actions.createTicket')}
        onSuccess={async (conversationId) => {
          setNewTicketModalOpen(false);

          try {
            const { data: tickets } = await dataProvider.getList('ticket', {
              pagination: { page: 1, perPage: 1 },
              filter: { conversationId_eq: conversationId },
              sort: { field: 'id', order: 'DESC' },
            });

            if (tickets && tickets.length > 0) {
              handleTicketSelect(tickets[0] as Ticket);
            }
          } catch (error) {
            console.error('Failed to fetch created ticket:', error);
          }
        }}
      />

      {/* 1. Persistent Search + New Ticket */}
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          alignItems: 'center',
          p: 1.5,
          pb: 1,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder={translate('resources.tickets.search.placeholder')}
          value={searchInput}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            endAdornment: searchInput && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleClearSearch} edge="end">
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: 'background.default',
              height: 36,
            },
          }}
        />
        <Button
          variant="outlined"
          color="primary"
          size="small"
          onClick={() => setNewTicketModalOpen(true)}
          sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
        >
          {translate('resources.tickets.buttons.newTicket')}
        </Button>
      </Box>

      {/* 2. Assignment-based Tabs */}
      <Tabs
        value={tab}
        onChange={handleChangeTab}
        variant="fullWidth"
        indicatorColor="primary"
        textColor="primary"
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          minHeight: 44,
          '& .MuiTab-root': {
            minHeight: 44,
            py: 1,
            textTransform: 'none',
            fontSize: '0.85rem',
            fontWeight: 500,
            minWidth: 0,
          },
        }}
      >
        <Tab
          value={TicketTabFilter.MINE}
          icon={<PersonIcon fontSize="small" />}
          iconPosition="start"
          label={translate('resources.tickets.tabs.mine')}
        />
        <Tab
          value={TicketTabFilter.UNASSIGNED}
          icon={<PeopleOutlineIcon fontSize="small" />}
          iconPosition="start"
          label={translate('resources.tickets.tabs.unassigned')}
        />
        <Tab
          value={TicketTabFilter.ALL}
          icon={<InboxIcon fontSize="small" />}
          iconPosition="start"
          label={translate('resources.tickets.tabs.all')}
        />
      </Tabs>

      {/* 3. Sort + Filter Toggle */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 1.5,
          py: 0.5,
          gap: 1,
          borderBottom: 1,
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        {/* Sort dropdown */}
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <Select
            value={sortField}
            onChange={(e: SelectChangeEvent) => setSortField(e.target.value as any)}
            startAdornment={<SortIcon fontSize="small" sx={{ mr: 0.5, color: 'action.active' }} />}
            sx={{ fontSize: '0.8rem', height: 32 }}
          >
            <MenuItem value="updatedAt">{translate('resources.tickets.sort.lastActivity')}</MenuItem>
            <MenuItem value="createdAt">{translate('resources.tickets.sort.createdDate')}</MenuItem>
            <MenuItem value="priority">{translate('resources.tickets.sort.priority')}</MenuItem>
          </Select>
        </FormControl>

        <Box sx={{ flex: 1 }} />

        {/* Filter toggle */}
        <Tooltip title={translate('resources.tickets.filters.advancedFilters')}>
          <IconButton
            size="small"
            onClick={() => setShowFilters(!showFilters)}
            color={activeFilterCount > 0 ? 'primary' : 'default'}
            sx={{
              bgcolor: showFilters ? 'action.selected' : 'transparent',
              position: 'relative',
            }}
          >
            <FilterListIcon fontSize="small" />
            {activeFilterCount > 0 && (
              <Chip
                label={activeFilterCount}
                size="small"
                color="primary"
                sx={{
                  position: 'absolute',
                  top: -4,
                  right: -4,
                  height: 16,
                  minWidth: 16,
                  fontSize: 10,
                  '& .MuiChip-label': { px: 0.5 },
                }}
              />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      {/* 4. Collapsible Advanced Filters */}
      <Collapse in={showFilters}>
        <Box sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: 'divider' }}>
          <Grid container spacing={1}>
            {/* Status filter */}
            <Grid size={{ xs: 6 }}>
              <FormControl fullWidth size="small">
                <Select
                  value={statusFilter || ''}
                  onChange={(e: SelectChangeEvent) => setStatusFilter(e.target.value ? (e.target.value as TicketStatus) : null)}
                  displayEmpty
                  sx={{ fontSize: '0.8rem', height: 32 }}
                >
                  <MenuItem value="">
                    <em>{translate('resources.tickets.filters.status')}</em>
                  </MenuItem>
                  <MenuItem value={TicketStatus.PENDING}>{translate('resources.ticket.status.pending')}</MenuItem>
                  <MenuItem value={TicketStatus.OPEN}>{translate('resources.ticket.status.open')}</MenuItem>
                  <MenuItem value={TicketStatus.RESOLVED}>{translate('resources.ticket.status.resolved')}</MenuItem>
                  <MenuItem value={TicketStatus.CLOSED}>{translate('resources.ticket.status.closed')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Priority filter */}
            <Grid size={{ xs: 6 }}>
              <FormControl fullWidth size="small">
                <Select
                  value={priorityFilter || ''}
                  onChange={(e: SelectChangeEvent) => setPriorityFilter(e.target.value ? (e.target.value as TicketPriority) : null)}
                  displayEmpty
                  sx={{ fontSize: '0.8rem', height: 32 }}
                >
                  <MenuItem value="">
                    <em>{translate('resources.tickets.filters.priority')}</em>
                  </MenuItem>
                  <MenuItem value={TicketPriority.LOW}>{translate('resources.ticket.priority.low')}</MenuItem>
                  <MenuItem value={TicketPriority.MEDIUM}>{translate('resources.ticket.priority.medium')}</MenuItem>
                  <MenuItem value={TicketPriority.HIGH}>{translate('resources.ticket.priority.high')}</MenuItem>
                  <MenuItem value={TicketPriority.URGENT}>{translate('resources.ticket.priority.urgent')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Queue filter */}
            <Grid size={{ xs: 12 }}>
              <TicketsQueueSelect
                selectedQueueIds={selectedQueueIds}
                userQueues={availableQueues}
                onChange={setSelectedQueueIds}
              />
            </Grid>
          </Grid>

          {activeFilterCount > 0 && (
            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <Button size="small" onClick={clearAllFilters} sx={{ fontSize: '0.75rem' }}>
                {translate('resources.tickets.filters.clearAll')}
              </Button>
            </Box>
          )}
        </Box>
      </Collapse>

      {/* 5. Single Tickets List */}
      <Box
        sx={{
          flex: 1,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <TicketsList
          tab={tab}
          searchParam={searchParam}
          statusFilter={statusFilter}
          priorityFilter={priorityFilter}
          selectedQueueIds={selectedQueueIds}
          sortField={sortField}
          sortOrder={sortOrder}
          onTicketSelect={handleTicketSelect}
          selectedTicketId={selectedTicketId}
          onContextMenu={handleContextMenu}
        />
      </Box>

      {/* 6. Context Menu */}
      <TicketContextMenu
        ticket={contextMenuTicket}
        anchorPosition={contextMenuPosition}
        onClose={handleCloseContextMenu}
      />
    </Paper>
  );
}

export default TicketsManager;
